import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { RingChart, Icon } from '@Components/AbwaabPlus'
import { useGetUserClassroomsQuery, useGetUserProfileQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { useGetLearningTimeQuery } from '@Features/Dashboard/Classrooms/Components/Tabs/SingleClassroomOverview/SingleClassroomSlice/SingleClassroomSlice'
import { formatDuration } from '@Utilities/Formatters'

/**
 * KPI strip — three at-a-glance metrics built from existing dashboard data
 * (no new endpoints): average completion (RingChart over enrolment
 * `pivot_progress`), enrolled-classroom count, and total learning time.
 */
const KpiStrip: React.FC = () => {
	const [t] = useTranslation('studentDashboard')
	const { data: classroomsData } = useGetUserClassroomsQuery()
	const { data: profile } = useGetUserProfileQuery()
	const userId = Number(profile?.data.student.user_id)
	const { data: learning } = useGetLearningTimeQuery({ user_id: userId }, { skip: !userId })

	const classrooms = useMemo(() => classroomsData?.data ?? [], [classroomsData])
	const count = classrooms.length

	const avgCompletion = useMemo(() => {
		if (!count) return 0
		const sum = classrooms.reduce((acc, c) => acc + (c.enrolled?.[0]?.pivot?.pivot_progress ?? 0), 0)
		let avg = sum / count
		// pivot_progress may be 0–1 or 0–100; normalise to a percentage.
		if (avg <= 1) avg = avg * 100
		return Math.min(Math.round(avg), 100)
	}, [classrooms, count])

	const totalSeconds = useMemo(() => {
		const d = learning?.data
		if (!d) return 0
		return (d.webinar ?? 0) + (d.test ?? 0) + (d.document ?? 0) + (d.video ?? 0) + (d.webcontent ?? 0) + (d.assignment ?? 0)
	}, [learning])

	return (
		<Strip>
			<Card>
				<RingChart value={avgCompletion} size={76} caption={t('completion', { defaultValue: 'الإكمال' })} />
				<Meta>
					<span className="label">{t('overallCompletion', { defaultValue: 'إجمالي التقدم' })}</span>
					<span className="sub">
						{count} {t('classrooms')}
					</span>
				</Meta>
			</Card>

			<Card>
				<IconTile>
					<Icon name="book-open" size={22} color="var(--blue)" />
				</IconTile>
				<Meta>
					<span className="value" dir="ltr">
						{count}
					</span>
					<span className="label">{t('myClassrooms')}</span>
				</Meta>
			</Card>

			<Card>
				<IconTile>
					<Icon name="trending-up" size={22} color="var(--blue)" />
				</IconTile>
				<Meta>
					{/* dir=auto: unit labels are localized, so ordering follows the label script */}
					<span className="value" dir="auto">
						{formatDuration(totalSeconds)}
					</span>
					<span className="label">{t('learningTime')}</span>
				</Meta>
			</Card>
		</Strip>
	)
}

export default KpiStrip

const Strip = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 0.75rem;
	width: 100%;
	@media (min-width: 768px) {
		grid-template-columns: repeat(3, 1fr);
		gap: 1rem;
	}
`

const Card = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: var(--r-xl);
	box-shadow: var(--sh-rest);
	padding: 14px 16px;
`

const IconTile = styled.span`
	width: 48px;
	height: 48px;
	flex: none;
	border-radius: var(--r-lg);
	background: var(--blue-12);
	display: flex;
	align-items: center;
	justify-content: center;
`

const Meta = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
	.value {
		font: var(--fw-bold) 20px var(--font-sans);
		color: var(--text-primary);
		line-height: 1.1;
	}
	.label {
		font: var(--fw-medium) 13px var(--font-sans);
		color: var(--text-primary);
	}
	.sub {
		font-size: 12px;
		color: var(--text-secondary);
		margin-top: 2px;
	}
`
